import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ShieldCheck, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public, unauthenticated download page for a Compliance Pack share link.
 * Reached via /compliance-pack/:token — hits /api/compliance-packs/public/:token.
 */
const CompliancePackPublic = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading', error: null });
  const [downloading, setDownloading] = useState(false);

  // Lightweight pre-flight HEAD-like check via a GET that streams the file.
  // Since the only API surface is the streamed download itself, we attempt
  // a small probe request that triggers a 4xx if invalid before user clicks.
  useEffect(() => {
    if (!token) {
      setState({ status: 'error', error: 'Invalid link' });
      return;
    }
    // Probe with a HEAD-like check using axios.get with range header (read only 1 byte)
    axios
      .get(`${API_URL}/compliance-packs/public/${token}`, {
        responseType: 'blob',
        headers: { Range: 'bytes=0-1' },
        validateStatus: () => true,
      })
      .then((res) => {
        if (res.status === 200 || res.status === 206) {
          setState({ status: 'ready', error: null });
        } else if (res.status === 410) {
          setState({ status: 'expired', error: 'This share link has expired.' });
        } else if (res.status === 404) {
          setState({ status: 'not_found', error: 'This share link is invalid or has been revoked.' });
        } else if (res.status === 400) {
          setState({ status: 'not_ready', error: 'Compliance pack is still being generated. Please try again in a moment.' });
        } else {
          setState({ status: 'error', error: `Unexpected error (${res.status}).` });
        }
      })
      .catch((err) => {
        setState({ status: 'error', error: err.message });
      });
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/compliance-packs/public/${token}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'compliance-pack.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Compliance pack downloaded');
    } catch (err) {
      toast.error('Download failed', { description: err.message });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full" data-testid="compliance-pack-public-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#ffd108]" />
            Compliance Pack
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state.status === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {state.status === 'ready' && (
            <>
              <p className="text-sm text-muted-foreground">
                Your compliance pack is ready. Click the button below to download the PDF.
              </p>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                data-testid="public-download-btn"
              >
                {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download PDF
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                This download link is shared by the company that scheduled the job.
              </p>
            </>
          )}
          {['expired', 'not_found', 'not_ready', 'error'].includes(state.status) && (
            <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="public-error">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="text-sm">{state.error}</p>
              {state.status === 'expired' && (
                <p className="text-xs text-muted-foreground">
                  Please contact the company that sent you this link to request a new one.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompliancePackPublic;
